import { ref, computed } from 'vue'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { api, ApiError } from '@/utils/api'
import i18n from '@/plugins/i18n'
import type {
  UserProfile,
  LoginResponse,
  RegisterResponse,
  TotpSetupResponse,
  TotpActionResponse,
} from '@/types'

export const useAuthStore = defineStore('auth', () => {
  // -- State --
  const user = ref<UserProfile | null>(null)
  const isLoading = ref(true)
  const error = ref<string | null>(null)

  // -- Getters --
  const isAuthenticated = computed(() => user.value !== null)
  const displayName = computed(() => user.value?.name ?? user.value?.username ?? '')
  const isTotpEnabled = computed(() => user.value?.totpEnabled ?? false)

  // -- Actions --

  /** Инициализация: проверяем авторизацию через /me */
  async function init(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      user.value = await api.get<UserProfile>('/api/v1/account/auth/me')
    } catch (e) {
      // 401 — не авторизован, это нормальное состояние
      user.value = null
      if (e instanceof ApiError && e.status !== 401) {
        error.value = e.message
      }
    } finally {
      isLoading.value = false
    }
  }

  /** Вход: email + password, опционально totpCode + totpSession */
  async function login(
    email: string,
    password: string,
    totpCode?: string,
    totpSession?: string,
  ): Promise<LoginResponse> {
    error.value = null
    const response = await api.post<LoginResponse>('/api/v1/account/auth/login', {
      email,
      password,
      totpCode,
      totpSession,
    })

    if (response.user) {
      user.value = response.user
    }

    return response
  }

  /** Регистрация */
  async function register(email: string, password: string, name: string): Promise<RegisterResponse> {
    error.value = null
    const localeRef = i18n.global.locale as unknown as { value: string }
    return api.post<RegisterResponse>('/api/v1/account/auth/register', {
      email,
      password,
      firstName: name.split(' ')[0] ?? '',
      lastName: name.split(' ')[1] ?? '',
      locale: localeRef.value,
    })
  }

  /** Выход */
  async function logout(): Promise<void> {
    error.value = null
    try {
      await api.post('/api/v1/account/auth/logout')
    } finally {
      user.value = null
    }
  }

  /** Получить текущий профиль */
  async function fetchMe(): Promise<void> {
    error.value = null
    try {
      user.value = await api.get<UserProfile>('/api/v1/account/auth/me')
    } catch (e) {
      user.value = null
      if (e instanceof ApiError && e.status !== 401) {
        error.value = e.message
      }
    }
  }

  /** Восстановление пароля */
  async function forgotPassword(email: string): Promise<void> {
    error.value = null
    await api.post('/api/v1/account/auth/forgot-password', { email })
  }

  /** Сброс пароля по токену */
  async function resetPassword(token: string, newPassword: string): Promise<void> {
    error.value = null
    await api.post('/api/v1/account/auth/reset-password', { token, newPassword })
  }

  /** Подтверждение email по токену */
  async function verifyEmail(token: string): Promise<void> {
    error.value = null
    await api.post('/api/v1/account/auth/verify-email', { token })
  }

  // -- TOTP Actions --

  /** Запросить настройку TOTP — возвращает секрет и URI для QR-кода */
  async function setupTotp(): Promise<TotpSetupResponse> {
    error.value = null
    return api.post<TotpSetupResponse>('/api/v1/account/auth/totp/setup')
  }

  /** Включить TOTP — подтверждение 6-значным кодом */
  async function enableTotp(code: string): Promise<TotpActionResponse> {
    error.value = null
    const response = await api.post<TotpActionResponse>('/api/v1/account/auth/totp/enable', {
      code,
    })
    if (response.success && user.value) {
      user.value = { ...user.value, totpEnabled: true }
    }
    return response
  }

  /** Отключить TOTP — требуется подтверждение паролем */
  async function disableTotp(password: string): Promise<TotpActionResponse> {
    error.value = null
    const response = await api.post<TotpActionResponse>('/api/v1/account/auth/totp/disable', {
      password,
    })
    if (response.success && user.value) {
      user.value = { ...user.value, totpEnabled: false }
    }
    return response
  }

  return {
    // State
    user,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    displayName,
    isTotpEnabled,
    // Actions
    init,
    login,
    register,
    logout,
    fetchMe,
    forgotPassword,
    resetPassword,
    verifyEmail,
    // TOTP
    setupTotp,
    enableTotp,
    disableTotp,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
